/**
 * ZIP / postcode check for USA, Canada and Australia receivers — the check
 * staff used to do on Google: does the ZIP belong to the typed state and town?
 * `info` is the backend's lookup (GET /countries/postal). Street addresses are
 * not checked.
 */
import { findStateOption, stateOptions } from "./stateCodes";

// "St. Louis" = "Saint Louis", "Ft Worth" = "Fort Worth", "Mt Vernon" = "Mount Vernon"
const normCity = (s) =>
    ` ${String(s || "").toUpperCase().replace(/[^A-Z0-9]+/g, " ")} `
        .replace(/ ST /g, " SAINT ")
        .replace(/ FT /g, " FORT ")
        .replace(/ MT /g, " MOUNT ")
        .replace(/\s+/g, "");

/** Town names with any "(district)" part removed, e.g. "Toronto (Harbourfront)" -> "Toronto". */
export const townNames = (info) => (info?.places || []).map((p) => p.replace(/\s*\(.*\)\s*$/, ""));

/** "Tennessee (TN)" for the ZIP's state(s). */
export function zipStateLabel(info, iso2) {
    return (info?.states || [])
        .map((s) => (stateOptions(iso2).find((o) => o.code === s.code) || { label: s.code }).label)
        .join(" / ");
}

/** True when the receiver's state isn't one the ZIP belongs to. */
export function stateMismatch(info, state, iso2) {
    if (!info?.found || !state) return false;
    const opt = findStateOption(state, iso2);
    return !!opt && !info.states.some((s) => s.code === opt.code);
}

/** True when the typed town isn't one of the ZIP's towns (US / Australia only). */
export function cityMismatch(info, city) {
    if (!info?.found || !info.city_check || !String(city || "").trim()) return false;
    return !info.places.some((p) => normCity(p) === normCity(city));
}

/**
 * Google Maps search link for an address (Maps URLs — no API key needed), so
 * staff can check the street exists and matches the town / state / ZIP.
 */
export function mapsSearchUrl(parts) {
    const query = (parts || []).map((p) => String(p || "").trim()).filter(Boolean).join(", ");
    return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : "";
}

/** Problems worth confirming before save, as sentences ([] = all good / can't tell). */
export function postalProblems(info, receiver, iso2) {
    if (!info?.supported || info.available === false) return [];
    const zip = String(receiver.pincode || "").trim();
    if (!info.format_ok) return [`"${zip}" doesn't look right: ${info.hint}.`];
    if (!info.found) return [`ZIP / postcode ${zip} wasn't found for ${iso2} — it may be mistyped.`];
    const out = [];
    if (stateMismatch(info, receiver.state, iso2)) {
        const typed = findStateOption(receiver.state, iso2);
        out.push(`${zip} is in ${zipStateLabel(info, iso2)}, but the state is ${typed ? typed.label : receiver.state}.`);
    }
    if (cityMismatch(info, receiver.city)) {
        out.push(`${zip} is ${townNames(info).slice(0, 4).join(" / ")}${info.places.length > 4 ? " …" : ""}, but the city is ${receiver.city}.`);
    }
    return out;
}
